import { useEffect, useState, useCallback } from "react";
import api, { money, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import PLSplitEditor from "@/components/PLSplitEditor";
import { StatusBadge } from "@/components/Bits";
import { CalendarBlank, Sparkle } from "@phosphor-icons/react";

const FREQS = ["Monthly", "Quarterly", "Annual", "Weekly", "Custom", "Full"];

function iso(d) { return d ? d.toISOString().slice(0, 10) : ""; }
function parseIso(s) { return s ? new Date(s + "T00:00:00") : null; }

export default function ReleaseScheduleModal({ item, open, onClose, onCreated }) {
  const [accounts, setAccounts] = useState([]);
  const [trackCats, setTrackCats] = useState([]);
  const [start, setStart] = useState(new Date());
  const [end, setEnd] = useState(() => { const d = new Date(); d.setFullYear(d.getFullYear() + 1); return d; });
  const [frequency, setFrequency] = useState("Monthly");
  const [basis, setBasis] = useState("monthly");
  const [rounding, setRounding] = useState("last");
  const [numPeriods, setNumPeriods] = useState(6);
  const [plCode, setPlCode] = useState("");
  const [plSplit, setPlSplit] = useState({ enabled: false, valid: true, pl_split: [] });
  const [tracking, setTracking] = useState({});
  const [description, setDescription] = useState("");
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiInfo, setAiInfo] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [splitSeed, setSplitSeed] = useState(null);
  const [splitKey, setSplitKey] = useState(0);

  useEffect(() => {
    if (!open || !item) return;
    // Reset every field to defaults so a freshly-opened item never inherits the
    // previous transaction's schedule settings.
    const today = new Date();
    const nextYear = new Date();
    nextYear.setFullYear(nextYear.getFullYear() + 1);
    setStart(today);
    setEnd(nextYear);
    setFrequency("Monthly");
    setBasis("monthly");
    setRounding("last");
    setNumPeriods(6);
    setPlCode("");
    setPlSplit({ enabled: false, valid: true, pl_split: [] });
    setSplitSeed(null);
    setSplitKey((k) => k + 1);
    setTracking({});
    setPreview(null);
    setAiInfo(null);
    setDescription(item.description || "");
    api.get(`/accounts?tenant_id=${item.tenant_id}`).then(({ data }) => {
      const cls = item.category === "DEFERRED_INCOME" ? "REVENUE" : "EXPENSE";
      const exp = data.accounts.filter((a) => a.class === cls);
      setAccounts(exp);
      if (exp[0]) setPlCode((p) => p || exp[0].code);
    });
    api.get(`/tracking-categories?tenant_id=${item.tenant_id}`).then(({ data }) => setTrackCats(data.categories));
    api.get("/schedule-templates").then(({ data }) =>
      setTemplates((data.templates || []).filter((t) => t.category === item.category))).catch(() => {});
  }, [open, item]);

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    const f = FREQS.find((x) => x.toLowerCase() === (t.frequency || "").toLowerCase());
    if (f) setFrequency(f);
    if (t.num_periods) setNumPeriods(t.num_periods);
    if (t.release_basis) setBasis(t.release_basis);
    if (t.pl_account_code) setPlCode(t.pl_account_code);
    if (t.description) setDescription(t.description);
    setSplitSeed(t.pl_split && t.pl_split.length >= 2 ? t.pl_split : null);
    setSplitKey((k) => k + 1);
    toast.success(`Applied template "${t.name}"`);
  };

  const saveTemplate = async () => {
    const name = window.prompt("Name this template (e.g. Annual insurance — 12 months):");
    if (!name || !name.trim()) return;
    try {
      const { data } = await api.post("/schedule-templates", {
        name: name.trim(), category: item.category, frequency: frequency.toLowerCase(),
        num_periods: Number(numPeriods), release_basis: basis,
        pl_account_code: plCode, description,
        pl_split: plSplit.enabled ? plSplit.pl_split : [],
      });
      setTemplates((ts) => [...ts, data.template]);
      toast.success("Template saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save template");
    }
  };

  const loadPreview = useCallback(async () => {
    if (!item) return;
    try {
      const { data } = await api.post("/schedules/preview", {
        original_amount: item.amount,
        start_date: iso(start),
        end_date: iso(end),
        frequency: frequency.toLowerCase(),
        num_periods: Number(numPeriods),
        category: item.category,
        release_basis: basis,
        rounding,
      });
      setPreview(data.preview);
    } catch { setPreview(null); }
  }, [item, start, end, frequency, numPeriods, basis, rounding]);

  useEffect(() => {
    const t = setTimeout(loadPreview, 200);
    return () => clearTimeout(t);
  }, [loadPreview]);

  if (!item) return null;

  const suggestWithAI = async () => {
    setAiLoading(true);
    setAiInfo(null);
    try {
      const { data } = await api.post("/schedules/ai-suggest", { item_id: item.id });
      const s = data.suggestion || {};
      if (s.start_date) setStart(parseIso(s.start_date));
      if (s.end_date) setEnd(parseIso(s.end_date));
      if (s.frequency) {
        const f = FREQS.find((x) => x.toLowerCase() === s.frequency.toLowerCase());
        if (f) setFrequency(f);
      }
      if (s.release_basis) setBasis(s.release_basis);
      if (s.num_periods) setNumPeriods(s.num_periods);
      if (s.pl_account_code) setPlCode(s.pl_account_code);
      if (s.description) setDescription(s.description);
      setAiInfo({
        reasoning: s.reasoning,
        plWarning: data.pl_warning,
        noDoc: data.no_document_note,
        sources: data.sources || [],
      });
      toast.success("AI filled the schedule — review and adjust before saving");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI suggestion failed");
    } finally { setAiLoading(false); }
  };

  const submit = async () => {
    if (!plCode) { toast.error("Select a P&L account code"); return; }
    if (plSplit.enabled && !plSplit.valid) { toast.error("Fix the P&L split — codes must be unique and total 100%"); return; }
    setSaving(true);
    try {
      await api.post("/schedules", {
        item_id: item.id,
        start_date: iso(start),
        end_date: iso(end),
        frequency: frequency.toLowerCase(),
        num_periods: Number(numPeriods),
        pl_account_code: plSplit.enabled && plSplit.pl_split[0] ? plSplit.pl_split[0].pl_account_code : plCode,
        release_basis: basis,
        rounding,
        pl_split: plSplit.enabled ? plSplit.pl_split : [],
        tracking: Object.entries(tracking).filter(([, v]) => v).map(([name, option]) => ({ name, option })),
        description,
      });
      toast.success("Release schedule created");
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl rounded-lg border-slate-200 p-0 gap-0 max-h-[92vh] overflow-y-auto">
        <DialogHeader className="px-7 pt-6 pb-4 border-b border-slate-100">
          <div className="overline text-orangeink mb-1">Release Schedule</div>
          <DialogTitle className="font-heading font-bold text-2xl tracking-tighter text-navy">
            {item.doc_number} · {item.contact}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Configure the release schedule for {item.doc_number}.
          </DialogDescription>
          <div className="flex items-center gap-3 mt-2 text-sm text-navy/70">
            <StatusBadge value={item.category} />
            <span className="font-mono">{item.account_code} {item.account_name}</span>
            <span className="font-mono font-semibold text-navy">{money(item.amount, item.currency)}</span>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 px-7 py-6">
          <div className="md:col-span-2 flex items-center justify-between gap-4 bg-orange-50 border border-orange-200 rounded-lg px-4 py-3">
            <div className="text-sm text-navy/70">
              <span className="font-semibold text-navy">Let AI draft this schedule.</span> It reads the Xero attachment (when available) and the item details to suggest the period, frequency and P&amp;L code.
            </div>
            <button onClick={suggestWithAI} disabled={aiLoading} data-testid="ai-suggest-btn"
              className="shrink-0 flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-[#F37A24] text-white hover:bg-navy transition-colors duration-200 disabled:opacity-60">
              <Sparkle size={16} weight="fill" />{aiLoading ? "Thinking…" : "Suggest with AI"}
            </button>
          </div>

          {aiInfo && (
            <div className="md:col-span-2 bg-navy/[0.03] border border-slate-200 rounded-lg p-4 space-y-2" data-testid="ai-suggest-info">
              {aiInfo.reasoning && (
                <div className="text-sm text-navy/75"><span className="overline text-navy/65 block mb-0.5">Why</span>{aiInfo.reasoning}</div>
              )}
              {aiInfo.noDoc && <div className="text-xs text-navy/70">{aiInfo.noDoc}</div>}
              {aiInfo.sources?.length > 0 && (
                <div className="text-xs text-navy/70">Read from: {aiInfo.sources.map((s) => `${s.doc_number} (${s.contact})`).join(", ")}</div>
              )}
              {aiInfo.plWarning && (
                <div className="text-xs font-medium text-[#B45309] bg-orange-50 border border-orange-200 rounded-md px-2.5 py-1.5" data-testid="ai-pl-warning">
                  ⚠ {aiInfo.plWarning}
                </div>
              )}
            </div>
          )}

          <div className="md:col-span-2 flex items-center gap-2 flex-wrap" data-testid="template-bar">
            <span className="overline text-navy/70">Template</span>
            <div className="flex-1 min-w-[200px]">
              <Select value="" onValueChange={applyTemplate}>
                <SelectTrigger data-testid="template-select" className="rounded-lg border-slate-200 h-9">
                  <SelectValue placeholder={templates.length ? "Apply a saved template…" : "No templates yet for this type"} />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} · {t.frequency}{t.num_periods ? ` ×${t.num_periods}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <button type="button" onClick={saveTemplate} data-testid="save-template-btn"
              className="px-3 py-2 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">
              Save current as template
            </button>
          </div>

          <DateField label="Start Date" value={start} onChange={setStart} testid="start-date" />
          <DateField label="End Date" value={end} onChange={setEnd} testid="end-date" />

          <div>
            <Lbl>Release Frequency</Lbl>
            <Select value={frequency} onValueChange={setFrequency}>
              <SelectTrigger data-testid="frequency-select" className="rounded-lg border-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FREQS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {frequency === "Custom" ? (
            <div>
              <Lbl>Number of Periods</Lbl>
              <input type="number" min={1} value={numPeriods} data-testid="num-periods-input"
                onChange={(e) => setNumPeriods(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none" />
            </div>
          ) : (
            <div>
              <Lbl>P&amp;L Account Code</Lbl>
              <Select value={plCode} onValueChange={setPlCode}>
                <SelectTrigger data-testid="pl-account-select" className="rounded-lg border-slate-200"><SelectValue placeholder="Select account" /></SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {frequency === "Custom" && (
            <div className="md:col-span-2">
              <Lbl>P&amp;L Account Code</Lbl>
              <Select value={plCode} onValueChange={setPlCode}>
                <SelectTrigger data-testid="pl-account-select" className="rounded-lg border-slate-200"><SelectValue placeholder="Select account" /></SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="md:col-span-2">
            <Lbl>Release basis</Lbl>
            <div className="flex border border-slate-200 rounded-lg overflow-hidden text-sm w-fit">
              {[{ v: "monthly", l: "Even periods" }, { v: "daily", l: "Daily pro-rata" }].map((b) => (
                <button key={b.v} type="button" data-testid={`release-basis-${b.v}`} onClick={() => setBasis(b.v)}
                  className={`px-4 py-2 font-medium transition-colors border-l border-slate-100 first:border-l-0 ${
                    basis === b.v ? "bg-orange text-white" : "bg-white text-navy/70 hover:bg-navy/5"}`}>
                  {b.l}
                </button>
              ))}
            </div>
            <p className="text-xs text-navy/65 mt-1.5">
              {basis === "daily"
                ? "Each journal is sized by the actual number of days it covers (accurate for part-months)."
                : "The total is split evenly across every release period."}
            </p>
          </div>

          {basis !== "daily" && (
            <div className="md:col-span-2">
              <Lbl>Rounding adjustment</Lbl>
              <div className="flex border border-slate-200 rounded-lg overflow-hidden text-sm w-fit">
                {[{ v: "last", l: "Last period" }, { v: "first", l: "First period" }].map((b) => (
                  <button key={b.v} type="button" data-testid={`rounding-${b.v}`} onClick={() => setRounding(b.v)}
                    className={`px-4 py-2 font-medium transition-colors border-l border-slate-100 first:border-l-0 ${
                      rounding === b.v ? "bg-orange text-white" : "bg-white text-navy/70 hover:bg-navy/5"}`}>
                    {b.l}
                  </button>
                ))}
              </div>
              <p className="text-xs text-navy/65 mt-1.5">
                Any rounding difference (so periods sum exactly to the total) is placed on this period.
              </p>
            </div>
          )}

          {trackCats.map((tc) => (
            <div key={tc.name}>
              <Lbl>Tracking · {tc.name}</Lbl>
              <Select value={tracking[tc.name] || ""} onValueChange={(v) => setTracking({ ...tracking, [tc.name]: v })}>
                <SelectTrigger data-testid={`tracking-${tc.name}`} className="rounded-lg border-slate-200"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  {tc.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ))}

          <PLSplitEditor key={splitKey} plAccounts={accounts} total={item.amount || 0}
            primaryCode={plCode} initialSplit={splitSeed} onChange={setPlSplit} />

          <div className="md:col-span-2">
            <Lbl>Description</Lbl>
            <Textarea data-testid="description-input" value={description} onChange={(e) => setDescription(e.target.value)}
              rows={2} className="rounded-lg border-slate-200 focus:border-orange" placeholder="Journal narration" />
          </div>
        </div>

        {preview && (
          <div className="px-7 pb-5">
            <div className="bg-navy/[0.03] border border-slate-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="overline text-navy/65">Calculated schedule</div>
                <div className="text-sm font-mono text-navy/70">
                  {preview.periods} periods · {preview.release_basis === "daily" ? "daily pro-rata" : `${money(preview.per_period_amount, item.currency)} / period`} · ends {fmtDate(preview.final_date)}
                </div>
              </div>
              {preview.rounding_adjustment !== 0 && preview.release_basis !== "daily" && (
                <div data-testid="rounding-note" className="text-xs text-navy/70 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3">
                  Rounding: {preview.periods - 1} × {money(preview.base_per_period, item.currency)} + period {preview.rounding_period} carries {money(preview.base_per_period + preview.rounding_adjustment, item.currency)} ({preview.rounding_adjustment > 0 ? "+" : ""}{money(preview.rounding_adjustment, item.currency)}) so the schedule sums exactly to the total.
                </div>
              )}
              <div className="max-h-40 overflow-y-auto border border-slate-100 rounded-md">
                <table className="w-full text-xs font-mono tabular">
                  <thead className="bg-navy/[0.04] text-navy/70 overline">
                    <tr><th className="text-left px-3 py-1.5">#</th><th className="text-left px-3 py-1.5">Journal Date</th><th className="text-right px-3 py-1.5">Amount</th></tr>
                  </thead>
                  <tbody>
                    {preview.journals.map((j) => {
                      const adj = j.period === preview.rounding_period && preview.rounding_adjustment !== 0 && preview.release_basis !== "daily";
                      return (
                      <tr key={j.period} className={`border-t border-slate-100 ${adj ? "bg-amber-50" : ""}`} data-testid={adj ? "rounding-row" : undefined}>
                        <td className="px-3 py-1.5 text-navy/70">{j.period}</td>
                        <td className="px-3 py-1.5">{fmtDate(j.date)}</td>
                        <td className={`px-3 py-1.5 text-right ${adj ? "font-semibold text-orangeink" : ""}`}>{money(j.amount, item.currency)}{adj ? " ▾" : ""}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 px-7 py-4 border-t border-slate-100 sticky bottom-0 bg-white">
          <button onClick={onClose} data-testid="cancel-schedule-btn"
            className="px-5 py-2.5 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="release-schedule-submit"
            className="px-6 py-2.5 text-sm font-medium rounded-lg bg-orange text-white hover:bg-navy transition-colors duration-200 disabled:opacity-60">
            {saving ? "SAVING…" : "Create Schedule"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Lbl({ children }) { return <span className="overline text-navy/70 block mb-1.5">{children}</span>; }

function DateField({ label, value, onChange, testid }) {
  return (
    <div>
      <Lbl>{label}</Lbl>
      <Popover>
        <PopoverTrigger asChild>
          <button data-testid={`${testid}-trigger`}
            className="w-full flex items-center justify-between bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-navy hover:border-navy focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none">
            {fmtDate(value)}
            <CalendarBlank size={16} className="text-navy/70" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 rounded-lg" align="start">
          <Calendar mode="single" selected={value} onSelect={(d) => d && onChange(d)}
            captionLayout="dropdown-buttons" fromYear={2015} toYear={new Date().getFullYear() + 10}
            defaultMonth={value || new Date()} initialFocus />
        </PopoverContent>
      </Popover>
    </div>
  );
}
